import React, { Key, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Form,
  FormTitle,
  InputNumber,
  message,
  Select,
  SubContent,
  Tag,
  Tooltip,
  Upload,
  useBreadcrumbRoutes,
  useUpdate,
  Wrapper,
  Tabs,
  Modal,
} from '@maxtropy/components';
import { Col, Row, Space } from 'antd';
import { config } from '@/pages/EnergyStatisticalReport/components/Config/constant';
import { AnalysisUnit } from '@/pages/MapConfiguration/EnergyOverview/components/BindUnitModal';
import { BindingUnit, unitDataType } from '@/pages/EnergyStatisticalReport/components/Config/BindingUnit';
import DeptAreaList, { DeptAreaListItem } from '@/pages/EnergyStatisticalReport/components/Config/DeptAreaList';
import {
  apiV2EnergyAnalysisListByOuPost,
  apiV2EnergyOverviewMediumByEnergyUnitPost,
  apiV2EnergyReportGasImportPost,
  apiV2EnergyReportTotalConfigDetailPost,
  apiV2EnergyReportTotalConfigSavePost,
} from '@maxtropy/device-customer-apis-v2';
import { DefaultOptionType } from 'rc-select/lib/Select';
import { useNavigate } from 'react-router-dom';
import { V2EnergyReportTotalConfigDetailPostResponse } from '@maxtropy/device-customer-apis-v2/dist/ytt/apis/v2/energyReportTotalConfigDetail';
import { isNil } from 'lodash-es';
import './index.module.scss';
import { InfoCircleOutlined } from '@ant-design/icons';

type FormItem = NonNullable<V2EnergyReportTotalConfigDetailPostResponse['factoryConfigs']>[number];
type FormValues = {
  [key: number]: FormItem;
  fileKey: string;
  deptList: DeptAreaListItem[];
  areaList: DeptAreaListItem[];
};

interface Props {}

const Config: React.FC<Props> = () => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<FormValues>();

  const breadcrumbRoutes = useBreadcrumbRoutes();
  const routesTitle = useMemo(() => ({ name: `配置` }), []);
  const navigate = useNavigate();
  const [update, updateFn] = useUpdate();

  const [isEdit, setIsEdit] = useState(false);
  const [configVisible, setConfigVisible] = useState(false);
  const [selectType, setSelectType] = useState<number>(-1);
  const originalData = useRef<Record<number, FormItem>>();

  // 部门/区域表格展示的能源类型指标：电(0)、水(2)、二氧化碳(4)、天然气(5)、液氧(6)、液氩(7)、压缩空气(8)
  // 文描仅作用于部门/区域（全厂配置仍使用 constant 中的 市电/自来水）
  const allEnergyConfigs = useMemo(() => {
    const allowedTypes = [0, 2, 4, 5, 6, 7, 8];
    const labelOverride: Record<number, string> = { 0: '电', 2: '水' };
    return config
      .flatMap(item => item.children)
      .filter(c => allowedTypes.includes(c.type))
      .map(c => ({ ...c, label: labelOverride[c.type] ?? c.label }));
  }, []);

  // Tab 状态
  const [activeKey, setActiveKey] = useState<string>('factory');

  // 用能分析组列表（用于全厂配置弹窗）
  const [unitGroup, setUnitGroup] = useState<AnalysisUnit[]>([]);
  // 已经选中的用能单元
  const [selectedUnit, setSelectedUnit] = useState<unitDataType | undefined>();
  // 能源介质options
  const [mediumOptionsMap, setMediumOptionsMap] = useState<Record<number, DefaultOptionType[]>>({});

  // 获取详情
  useEffect(() => {
    setLoading(true);
    apiV2EnergyReportTotalConfigDetailPost({})
      .then(async res => {
        const factoryConfigs = res?.factoryConfigs || [];
        const deptConfigs = res?.deptConfigs || [];
        const areaConfigs = res?.areaConfigs || [];

        // 收集所有的 fetchMediumByUnitId Promise
        const fetchPromises: Promise<void>[] = [];

        // 处理全厂配置
        const factoryDetail = factoryConfigs.reduce<Record<number, FormItem>>((prev, item) => {
          const { energyUnitGroupId, energyUnitId, type } = item;
          if (energyUnitGroupId && energyUnitId && !isNil(type)) {
            prev[type] = item;
            fetchPromises.push(fetchMediumByUnitId(energyUnitId, type));
          }
          return prev;
        }, {});

        // 处理部门配置 - 转换为 deptList 格式
        const deptList = deptConfigs.map(item => ({
          id: String(item.deptAreaId),
          name: item.name,
          configs: allEnergyConfigs.map(config => {
            const foundConfig = item.configs?.find(c => c.type === config.type);
            return {
              type: config.type,
              energyUnitGroupId: foundConfig?.energyUnitGroupId,
              energyUnitId: foundConfig?.energyUnitId,
              energyUnitName: foundConfig?.energyUnitName,
              mediumId: foundConfig?.mediumId,
              coefficient: foundConfig?.coefficient ?? 1,
            };
          }),
        }));

        // 处理区域配置 - 转换为 areaList 格式
        const areaList = areaConfigs.map(item => ({
          id: String(item.deptAreaId),
          name: item.name,
          configs: allEnergyConfigs.map(config => {
            const foundConfig = item.configs?.find(c => c.type === config.type);
            return {
              type: config.type,
              energyUnitGroupId: foundConfig?.energyUnitGroupId,
              energyUnitId: foundConfig?.energyUnitId,
              energyUnitName: foundConfig?.energyUnitName,
              mediumId: foundConfig?.mediumId,
              coefficient: foundConfig?.coefficient ?? 1,
            };
          }),
        }));

        const init = { ...getInitialValue(), ...factoryDetail, deptList, areaList };
        originalData.current = init;
        form.setFieldsValue(init);

        // 介质选择options
        await Promise.all(fetchPromises);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [update, allEnergyConfigs]);

  // 获取能源介质by unitId
  const fetchMediumByUnitId = async (unitId: number, childType: number) => {
    if (!unitId) {
      setMediumOptionsMap(prev => ({ ...prev, [childType]: [] }));
      return;
    }
    const res = await apiV2EnergyOverviewMediumByEnergyUnitPost({ list: [unitId] });
    const options = res?.list?.map(item => ({ label: item.energyName!, value: item.id! }));
    setMediumOptionsMap(prev => ({ ...prev, [childType]: options ?? [] }));
  };

  // 表单初始值
  const getInitialValue = () => {
    return config.reduce<Record<number | string, FormItem>>((prev, item) => {
      item.children.forEach(child => {
        prev[child.type] = {
          type: child.type,
          energyUnitGroupId: undefined,
          energyUnitId: undefined,
          mediumId: undefined,
          energyUnitName: undefined,
        };
      });
      return prev;
    }, {});
  };

  // 配置button点击
  const handleConfigButtonClick = (type: number) => {
    setConfigVisible(true);
    setSelectType(type);
    const energyUnitGroupId = form.getFieldValue([type, 'energyUnitGroupId']);
    if (energyUnitGroupId) {
      setSelectedUnit({
        energyUnitGroupId: Number(energyUnitGroupId),
        energyUnitList: [
          {
            energyUnitIds: Number(form.getFieldValue([type, 'energyUnitId'])),
            energyUnitName: form.getFieldValue([type, 'energyUnitName']),
          },
        ],
      });
    }
  };

  // 配置弹窗确认
  const handleConfigModalOk = (values: any) => {
    const { energyUnitGroupId, energyUnitList } = values;

    const prevUnitId = form.getFieldValue([selectType, 'energyUnitId']);
    // 控制了单选, 但是返回形式还是arr, 所以取第一个
    const { energyUnitIds: unitId, energyUnitName } = energyUnitList[0] ?? {};
    setConfigFormItemValue(selectType, {
      energyUnitGroupId: Number(energyUnitGroupId),
      energyUnitId: Number(unitId),
      energyUnitName,
    });

    if (prevUnitId !== unitId) {
      resetMediumIdAndOptions(selectType);
      fetchMediumByUnitId(Number(unitId), selectType);
    }
    handleConfigModalCancel();
  };

  const handleConfigModalCancel = () => {
    setConfigVisible(false);
    setSelectedUnit(undefined);
  };

  // 提交
  const handleSubmit = async () => {
    if (!isEdit) {
      setIsEdit(true);
      return;
    }

    const formValues = form.getFieldsValue();
    const { fileKey, deptList, areaList, ...factoryValues } = formValues;

    // 全厂配置
    const electricityConfig = Object.entries(factoryValues)
      .filter(([key, value]) => {
        return value?.energyUnitId && value?.mediumId;
      })
      .map(([key, value]) => ({
        energyUnitId: value.energyUnitId,
        mediumId: value.mediumId,
        coefficient: value.coefficient,
        type: Number(key),
      }));

    // 部门配置
    const deptConfigs = (deptList || []).map((item: any) => ({
      timestamp: Date.now(),
      deptAreaId: item.isNew ? undefined : item.id ? Number(item.id) : undefined, // 新增不传 deptAreaId
      name: item.name,
      configs: (item.configs || [])
        .filter((config: any) => config?.energyUnitId)
        .map((config: any) => ({
          energyUnitId: config.energyUnitId,
          type: config.type,
        })),
    }));

    // 区域配置
    const areaConfigs = (areaList || []).map((item: any) => ({
      timestamp: Date.now(),
      deptAreaId: item.isNew ? undefined : item.id ? Number(item.id) : undefined, // 新增不传 deptAreaId
      name: item.name,
      configs: (item.configs || [])
        .filter((config: any) => config?.energyUnitId)
        .map((config: any) => ({
          energyUnitId: config.energyUnitId,
          type: config.type,
        })),
    }));

    setLoading(true);

    apiV2EnergyReportTotalConfigSavePost({ electricityConfig, deptConfigs, areaConfigs })
      .onError(error => {
        const { errorMessage } = error?.cause;
        Modal.error({
          title: errorMessage ?? '未知错误！ 请联系管理员。',
        });
        throw error;
      })
      .then(() => {
        if (fileKey) {
          return apiV2EnergyReportGasImportPost({ value: fileKey });
        }
      })
      .then(() => {
        message.success('保存成功');
        updateFn(); // 触发 useEffect 重新获取详情
        setIsEdit(false);
      })
      .catch(error => {
        setLoading(false); // 失败时关闭 loading
      });
  };

  const resetRowData = (type: number) => {
    setConfigFormItemValue(type);
    resetMediumIdAndOptions(type);
  };

  // 选了用能单位必须选介质
  const validateMedium = async (type: number, value: Key) => {
    const unitId = form.getFieldValue([type, 'energyUnitId']);
    if (!unitId) {
      return Promise.resolve();
    }
    return value ? Promise.resolve() : Promise.reject('请选择能源介质');
  };

  const setConfigFormItemValue = (type: number, value?: FormItem) => {
    value ??= {};
    const { energyUnitGroupId, energyUnitId, energyUnitName } = value;
    form.setFieldValue([type, 'energyUnitGroupId'], energyUnitGroupId ? Number(energyUnitGroupId) : undefined);
    form.setFieldValue([type, 'energyUnitId'], energyUnitId ? Number(energyUnitId) : undefined);
    form.setFieldValue([type, 'energyUnitName'], energyUnitName);
  };

  const resetMediumIdAndOptions = (type: number) => {
    form.setFieldValue([type, 'mediumId'], undefined);
    delete mediumOptionsMap[type];
    setMediumOptionsMap({ ...mediumOptionsMap });
    //清除校验错误
    clearMediumError([type]);
  };

  const clearMediumError = (types: number[]) => {
    const fields = types.map(type => ({ name: [type, 'mediumId'], errors: [] })) as any[];
    form.setFields(fields);
  };

  // 用能分析组数据（用于全厂配置弹窗）
  useEffect(() => {
    apiV2EnergyAnalysisListByOuPost({}).then(res => {
      setUnitGroup((res.list ?? []) as AnalysisUnit[]);
    });
  }, []);

  return (
    <Wrapper
      routes={[...(breadcrumbRoutes?.routes ?? []), routesTitle]}
      isLoading={loading}
      className={'report-config-wrapper'}
    >
      <Form form={form} initialValues={getInitialValue()} disabled={!isEdit} onFinish={handleSubmit}>
        <Tabs
          activeKey={activeKey}
          onChange={key => setActiveKey(key)}
          items={[
            {
              label: '全厂',
              key: 'factory',
              children: (
                <>
                  <FormTitle title="用能总量统计报表配置" />
                  {config.map((item, index) => {
                    return (
                      <SubContent title={item.title} className={'mb-8'}>
                        {item.children.map((child, childIndex) => {
                          return (
                            <Row key={child.type}>
                              <Col span={8}>
                                <Form.Item label={child.label} name={[child.type, 'energyUnitId']}>
                                  <Button type={'link'} onClick={() => handleConfigButtonClick(child.type)}>
                                    配置
                                  </Button>
                                  {form.getFieldValue([child.type, 'energyUnitName']) && (
                                    <Tag closable={isEdit} onClose={() => resetRowData(child.type)}>
                                      {form.getFieldValue([child.type, 'energyUnitName']) ?? ''}
                                    </Tag>
                                  )}
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item
                                  label={'能源介质选择'}
                                  name={[child.type, 'mediumId']}
                                  rules={[{ validator: (_, value: Key) => validateMedium(child.type, value) }]}
                                >
                                  <Select placeholder={'请选择'} options={mediumOptionsMap[child.type] || []} />
                                </Form.Item>
                              </Col>
                              <Col span={6}>
                                <Space align="baseline" size="small">
                                  <Form.Item
                                    label="系数设置"
                                    initialValue={1}
                                    name={[child.type, 'coefficient']}
                                    rules={[
                                      {
                                        validator: (_, value: Key) => {
                                          if (!value || (Number(value) > 0 && Number(value) <= 9999.99999999)) {
                                            return Promise.resolve();
                                          }
                                          return Promise.reject('请输入0-9999.99999999之间的数字');
                                        },
                                      },
                                    ]}
                                  >
                                    <InputNumber precision={8} style={{ width: '100px' }} />
                                  </Form.Item>
                                  <Tooltip
                                    title={`${child.label}的展示单位为${child.unit}，${child.label}的用量（${child.unit}）=对应用能单元的介质用量*系数`}
                                  >
                                    <InfoCircleOutlined
                                      style={{ color: '#f0cd05ff', cursor: 'pointer', marginLeft: 8 }}
                                    />
                                  </Tooltip>
                                </Space>
                              </Col>
                            </Row>
                          );
                        })}
                      </SubContent>
                    );
                  })}
                  <SubContent title={'数据补录配置'} className={'mb-8'}>
                    <div style={{ position: 'relative' }}>
                      <Form.Item label="配置表" name="fileKey" wrapperCol={{ span: 8 }}>
                        <Upload
                          listType="text"
                          accept={['.xlsx', '.xls']}
                          uploadText="点击上传"
                          fileSize={20}
                          tip="支持扩展名：.xls、.xlsx"
                          disabled={!isEdit}
                          className={isEdit ? '' : 'not-allow-upload'}
                        />
                      </Form.Item>
                      <a
                        href={`/api/v2/energy-report/gas-download`}
                        style={{ position: 'absolute', left: 255, top: 5 }}
                      >
                        {isEdit ? '下载已上传配置表' : '下载配置表'}
                      </a>
                    </div>
                  </SubContent>
                </>
              ),
            },
            {
              label: '部门',
              key: 'dept',
              forceRender: true,
              children: <DeptAreaList type="dept" isEdit={isEdit} form={form} configList={allEnergyConfigs} />,
            },
            {
              label: '区域',
              key: 'area',
              forceRender: true,
              children: <DeptAreaList type="area" isEdit={isEdit} form={form} configList={allEnergyConfigs} />,
            },
          ]}
        />

        {/* 公共底部按钮区域 */}
        <Space className="sticky-footer" size={8}>
          <Button
            disabled={false}
            type="primary"
            onClick={() => {
              if (isEdit) {
                form.submit();
              } else {
                setIsEdit(true);
              }
            }}
          >
            {isEdit ? '保存' : '编辑'}
          </Button>
          <Button
            disabled={false}
            onClick={() => {
              if (isEdit) {
                setIsEdit(false);
                updateFn();
                const types = config.flatMap(curr => curr.children.map(i => i.type));
                clearMediumError(types);
              } else {
                navigate('/energy/statistic/report');
              }
            }}
          >
            {isEdit ? '取消' : '返回'}
          </Button>
        </Space>
      </Form>

      {configVisible && (
        <BindingUnit
          value={selectedUnit}
          unitGroup={unitGroup}
          onChange={handleConfigModalOk}
          onCancel={handleConfigModalCancel}
        />
      )}
    </Wrapper>
  );
};

export default Config;
